<template>
  <div id="user-form">
    <h3 class="mt-2">{{ this.entity.id ? "Edit" : "Create" }} {{ $t("systemUserForm.user") }} {{ entity.name }}</h3>
    <BButtonToolbar class="mb-2 pt-2 pb-2 border-light border-top border-bottom">
      <BButtonGroup>
        <BButton size="sm" variant="outline-primary" @click="onClose" class="custom-button">{{
          $t("systemUserForm.close")
        }}</BButton>
        <BButton
          :disabled="!isDirty"
          class="ml-2"
          size="sm"
          variant="primary"
          @click="onSave"
          style="background-color: #0054a6; color: white"
        >
          <font-awesome-icon icon="save"></font-awesome-icon>
          {{ $t("systemUserForm.save") }}
        </BButton>
      </BButtonGroup>
    </BButtonToolbar>

    <div class="row">
      <div class="col-8">
        <BForm ref="form">
          <BFormGroup id="input-group-1" description="Email should be unique." label="Email:" label-for="input-1">
            <BFormInput
              id="input-1"
              v-model="entity.email"
              placeholder="Enter Email"
              required
              type="email"
            ></BFormInput>
          </BFormGroup>

          <BFormGroup id="input-group-2" label="Name:" label-for="input-2">
            <BFormInput id="input-2" v-model="entity.name" placeholder="Enter Name" type="text"></BFormInput>
          </BFormGroup>

          <BFormGroup id="input-group-3" label="User Name:" label-for="input-3">
            <BFormInput id="input-3" v-model="entity.userName" placeholder="Enter User Name" type="text"></BFormInput>
          </BFormGroup>

          <BFormGroup id="input-group-password" label="Password:" label-for="input-password" v-if="isCreateMode">
            <div class="position-relative">
              <BFormInput
                id="input-password"
                v-model="password"
                required
                :type="showPassword ? 'text' : 'password'"
                @focus="passwordFocused = true"
                @blur="passwordFocused = false"
              />
              <font-awesome-icon
                v-if="passwordFocused"
                :icon="showPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"
                class="eye-icon"
                @mousedown.prevent="togglePassword"
              />
            </div>
          </BFormGroup>
        </BForm>
      </div>
      <!-- /.col-8 -->
      <div class="col-4">
        <FormList :data="roles" :disabled="isCreateMode" @removed="onRemoveRole" @selected="onSelectRole">
          <h5>{{ $t("systemUserForm.roles") }}</h5>
        </FormList>
        <hr />
        <PermissionsPanel
          :disabled="isCreateMode || !canModifyPermissions"
          provider-name="U"
          :provider-key="id"
          v-if="formReady"
        />
      </div>
      <!-- /.col-4 -->
    </div>
    <!-- /.row -->
    <ItemSelectModal
      ref="roleSelectModal"
      :entity-type-name="'Role'"
      :items="availableRoles"
      :window-id="'roles'"
      :value-field="'name'"
      @itemSelected="onRoleSelected"
    ></ItemSelectModal>
  </div>
</template>

<script lang="ts">
import { Inject } from "inversify-props";
import { UserApi } from "@/api";
import {
  IdentityRoleDto,
  IdentityRoleDtoListResultDto,
  IdentityUserCreateDto,
  IdentityUserDto,
  IdentityUserUpdateDto
} from "@/api/model";
import BaseFormMixin from "@/core/views/forms/BaseForm";
import { AxiosResponse } from "axios";
import FormList from "@/core/views/pages/_components/FormList.vue";
import ItemSelectModal from "@/core/views/pages/_components/ItemSelectModal.vue";
import AuditFormBlock from "@/core/views/pages/_components/AuditFormBlock.vue";
import PermissionsPanel from "@/core/views/pages/SaaS/_components/PermissionsPanel.vue";
import { PermissionGuard } from "@/core/plugins/permissions";
import { mixins, Options } from "vue-class-component";
import { BButton, BButtonGroup, BButtonToolbar, BForm, BFormInput, BFormGroup } from "bootstrap-vue-next";

@Options({
  components: {
    PermissionsPanel,
    AuditFormBlock,
    ItemSelectModal,
    FormList,
    BButton,
    BButtonGroup,
    BButtonToolbar,
    BForm,
    BFormInput,
    BFormGroup
  }
})
export default class SystemUserForm extends mixins(
  BaseFormMixin<IdentityUserDto, IdentityUserCreateDto, IdentityUserUpdateDto>
) {
  @Inject("UserApi")
  private readonly usersApi!: UserApi;

  meta = {
    gridRoute: "SystemUsersGrid",
    formEditRoute: "SystemUserFormEdit",
    entityTypeName: "System User"
  };
  protected roles: IdentityRoleDto[] = [];
  protected availableRoles: IdentityRoleDto[] = [];
  private password = "";
  private showPassword = false;
  passwordFocused = false;

  get canModifyPermissions(): boolean {
    return PermissionGuard("AbpIdentity.Users.ManagePermissions");
  }

  async onEntityLoaded(): Promise<void> {
    if (this.id) {
      await this.usersApi
        .apiIdentityUsersIdRolesGet(this.id)
        .then((response: AxiosResponse<IdentityRoleDtoListResultDto>) => {
          this.roles = response.data.items as IdentityRoleDto[];
        });
    }
    this.formReady = true;
  }

  mapToCreateUpdateDto(entity: IdentityUserDto): IdentityUserDto {
    return entity;
  }

  getRequest(id: string): Promise<AxiosResponse<IdentityUserDto>> {
    return this.usersApi.apiIdentityUsersIdGet(id);
  }

  createRequest(entity: IdentityUserDto): Promise<AxiosResponse<IdentityUserDto>> {
    return this.usersApi.apiIdentityUsersPost(this.mapToCreateDto(entity));
  }

  updateRequest(id: string, entity: IdentityUserDto): Promise<AxiosResponse<IdentityUserDto>> {
    return this.usersApi.apiIdentityUsersIdPut(id, this.mapToUpdateDto(entity));
  }

  mapToCreateDto(entity: IdentityUserDto): IdentityUserCreateDto {
    return {
      email: entity.email,
      name: entity.name,
      userName: entity.userName,
      password: this.password
    } as IdentityUserCreateDto;
  }

  mapToUpdateDto(entity: IdentityUserDto): IdentityUserUpdateDto {
    return {
      email: entity.email,
      name: entity.name,
      userName: entity.userName,
      password: undefined,
      lockoutEnabled: entity.lockoutEnabled,
      phoneNumber: entity.phoneNumber,
      roleNames: this.roles.map(r => r.name),
      surname: entity.surname,
      concurrencyStamp: entity.concurrencyStamp
    } as IdentityUserUpdateDto;
  }

  async onSelectRole(): Promise<void> {
    await this.usersApi
      .apiIdentityUsersAssignableRolesGet()
      .then((response: AxiosResponse<IdentityRoleDtoListResultDto>) => {
        this.availableRoles = response.data.items as IdentityRoleDto[];
        const modal = this.$refs.roleSelectModal as InstanceType<typeof ItemSelectModal>;
        modal.open();
      });
  }

  async onRoleSelected(name: string): Promise<void> {
    const item = this.availableRoles.find(x => x.name === name) as IdentityRoleDto;
    if (item) {
      this.roles.push(item);
      await this.onSave();
    }
  }

  async onRemoveRole(name: string): Promise<void> {
    const item = this.roles.find(x => x.name === name) as IdentityRoleDto;
    if (item) {
      const idx = this.roles.indexOf(item);
      this.roles.splice(idx, 1);
      await this.onSave();
    }
  }
  togglePassword() {
    this.showPassword = !this.showPassword;
  }
}
</script>
<style scoped>
.custom-button {
  color: #0054a6;
  background-color: white;
  border-color: #0054a6;
  transition: all 0.02s ease;
}

.custom-button:hover {
  color: white !important;
  background-color: #0054a6 !important;
  border-color: #0054a6;
}
#user-form {
  font-size: 14px;
  font-family: Arial, sans-serif;
}
.eye-icon {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  cursor: pointer;
  font-size: 1.2rem;
  color: #6c757d;
}
</style>
